// Universal "Back" navigation + breadcrumb shown on every non-home public page.
// — Click "Back": uses browser history if the user came from within the site,
//   else falls back to "/" (Home).
// — Renders a Home → Current Page breadcrumb (multi-level when path is nested).
// — Injects a BreadcrumbList JSON-LD for Google rich results.
import { useEffect, useMemo, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronRight, ArrowLeft, Home as HomeIcon } from "lucide-react";

// Pretty-print a path segment: "cbse-tuition" → "CBSE Tuition".
// Known acronyms are preserved in uppercase.
const ACRONYMS = new Set([
  "cbse", "icse", "isc", "ib", "ibdp", "igcse", "nios",
  "neet", "jee", "ntse", "kvpy", "olympiad",
  "ssc", "hsc", "puc", "iit", "aiims",
  "faq", "faqs", "seo", "geo", "aeo",
  "uht", "qr", "upi", "url", "uk", "us", "usa", "uae", "nri",
]);
const titleize = (slug) =>
  decodeURIComponent(slug)
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => (ACRONYMS.has(w.toLowerCase()) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ");

// Routes where the back-nav strip is intentionally NOT rendered.
const HIDDEN_EXACT = new Set(["/", "/login", "/signup", "/admin-login"]);
const HIDDEN_PREFIX = ["/admin"]; // admin uses its own shell

function shouldHide(pathname) {
  if (HIDDEN_EXACT.has(pathname)) return true;
  return HIDDEN_PREFIX.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

export default function BackNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const internalNavCount = useRef(0);

  // Count in-app navigations so we know if there's history to go back to.
  useEffect(() => {
    internalNavCount.current += 1;
  }, [location.pathname]);

  const segments = useMemo(() => {
    const parts = location.pathname.split("/").filter(Boolean);
    let acc = "";
    return parts.map((p) => {
      acc += "/" + p;
      return { slug: p, href: acc, label: titleize(p) };
    });
  }, [location.pathname]);

  // BreadcrumbList JSON-LD (helps Google show rich breadcrumbs in search).
  useEffect(() => {
    if (shouldHide(location.pathname)) return undefined;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const itemListElement = [
      { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
      ...segments.map((s, i) => ({
        "@type": "ListItem",
        position: i + 2,
        name: s.label,
        item: `${origin}${s.href}`,
      })),
    ];
    const ld = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement,
    };
    document.querySelectorAll('script[data-uht-ld="backnav-breadcrumb"]').forEach((n) => n.remove());
    const tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.setAttribute("data-uht-ld", "backnav-breadcrumb");
    tag.text = JSON.stringify(ld);
    document.head.appendChild(tag);
    return () => {
      document.querySelectorAll('script[data-uht-ld="backnav-breadcrumb"]').forEach((n) => n.remove());
    };
  }, [location.pathname, segments]);

  if (shouldHide(location.pathname)) return null;

  const handleBack = () => {
    // If we've navigated at least once inside the app, go back. Otherwise home.
    if (internalNavCount.current > 1 && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  return (
    <nav
      aria-label="Breadcrumb"
      className="sticky top-[7rem] sm:top-[9rem] lg:top-[10rem] z-30 w-full bg-white/85 backdrop-blur-xl border-b border-slate-200/60"
      data-testid="back-nav"
    >
      <div className="max-w-7xl mx-auto px-4 h-11 flex items-center gap-3">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-1.5 text-[12.5px] sm:text-sm font-medium text-brand-royal hover:text-brand-cyan transition shrink-0 group"
          data-testid="back-button"
          aria-label="Go back"
        >
          <ArrowLeft size={15} className="transition-transform group-hover:-translate-x-0.5" />
          <span className="hidden sm:inline">Back</span>
        </button>

        <span className="h-4 w-px bg-slate-300/70 shrink-0" aria-hidden="true" />

        <ol className="flex items-center gap-1.5 text-[12.5px] sm:text-sm text-slate-600 min-w-0 overflow-x-auto no-scrollbar">
          <li className="shrink-0">
            <Link to="/" className="inline-flex items-center gap-1 text-slate-500 hover:text-brand-royal transition" data-testid="breadcrumb-home">
              <HomeIcon size={13} />
              <span>Home</span>
            </Link>
          </li>
          {segments.map((s, i) => {
            const isLast = i === segments.length - 1;
            return (
              <li key={s.href} className="flex items-center gap-1.5 shrink-0">
                <ChevronRight size={13} className="text-slate-400" aria-hidden="true" />
                {isLast ? (
                  <span className="text-brand-royal font-semibold truncate max-w-[60vw] sm:max-w-none" aria-current="page" data-testid="breadcrumb-current">
                    {s.label}
                  </span>
                ) : (
                  <Link to={s.href} className="text-slate-500 hover:text-brand-royal transition truncate max-w-[40vw] sm:max-w-none" data-testid={`breadcrumb-${s.slug}`}>
                    {s.label}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
}
